import { useId, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, MonitorSmartphone, ShieldCheck, Trash2 } from "lucide-react";
import { unwrap } from "@majourneys/api-client";
import { CONTROL_EDGE, CONTROL_EDGE_ON, CONTROL_LIT } from "@majourneys/shared/ui";
import { api } from "../../../lib/api";
import { authClient, signOut } from "../../../lib/auth";
import { panelStyle } from "../../../shared/ui/card-frame";
import { accountKeys } from "../constants/query-keys";

/* ------------------------------------------------------------------ */
/* Sign-in & security: how you sign in, other devices, and deleting   */
/* the account. Guests have no passwords (owner, 2026-10-12).          */
/* ------------------------------------------------------------------ */

const NAVY = "#110E39";
const MUTED = "#5a5d77";
const RED = "#9B2C2C";
const GREEN = "#1A7A44";
const GREEN_TINT = "rgba(31,168,85,0.13)";
const RED_TINT = "rgba(190,60,60,0.10)";
const SOFT_TINT = "rgba(17,14,57,0.055)";
const NAVY_GRADIENT = "linear-gradient(135deg, #2E2878 0%, #1A1454 48%, #110E39 100%)";
const CONTROL = { background: CONTROL_LIT, boxShadow: `0 0 0 1px ${CONTROL_EDGE}` } as const;

/**
 * The word that arms the delete button. Kept as the literal DELETE in every
 * language, the same as the app (delete-account-sheet.tsx): the label around
 * it translates, the word does not, so a translated word can never lock
 * someone out of deleting. Matched like the app does, trimmed and upper-cased.
 */
export const DELETE_WORD = "DELETE";

export type SignInMethod = "google" | "apple" | "code";

/**
 * Which way this account signs in, from better-auth's own list of linked
 * accounts. Google or Apple when linked; everyone else signs in with a code we
 * email. An old "credential" (password) row no longer counts: guests cannot
 * sign in with a password any more (packages/auth password-gate.ts).
 */
export function methodFromProviders(providers: string[]): SignInMethod {
  if (providers.includes("google")) return "google";
  if (providers.includes("apple")) return "apple";
  return "code";
}

function useSignInMethod() {
  return useQuery({
    queryKey: [...accountKeys.all, "sign-in-accounts"],
    queryFn: async (): Promise<SignInMethod> => {
      const res = await authClient.listAccounts();
      if (res.error) throw new Error(res.error.message ?? "listAccounts failed");
      return methodFromProviders((res.data ?? []).map((a) => a.providerId));
    },
    staleTime: 5 * 60_000,
  });
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="text-[11px] font-bold uppercase" style={{ color: MUTED, letterSpacing: "0.08em" }}>
      {children}
    </p>
  );
}

function Block({ children, testId, bare = false }: { children: ReactNode; testId?: string; bare?: boolean }) {
  // `bare`: already inside a pewter card (the account page's Account card),
  // where a second band would draw the outline twice. A hairline above
  // separates it instead.
  if (bare) {
    return (
      <section data-testid={testId} className="border-t border-[rgba(17,14,57,0.1)] pt-4">
        {children}
      </section>
    );
  }
  return (
    <section data-testid={testId} className="p-3.5" style={panelStyle(18)}>
      {children}
    </section>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

function HowYouSignIn({ email }: { email: string | null }) {
  const { t } = useTranslation();
  const method = useSignInMethod();
  // A failed lookup falls back to the code: every account can sign in that way.
  const kind: SignInMethod | null = method.isError ? "code" : (method.data ?? null);

  const label =
    kind === "google"
      ? t("account_modal.security_with_google")
      : kind === "apple"
        ? t("account_modal.security_with_apple")
        : t("account_modal.security_with_code");

  return (
    <Block testId="security-sign-in">
      <Eyebrow>{t("account_modal.security_how_title")}</Eyebrow>
      {kind === null ? (
        <div className="mt-3 flex items-center gap-3" aria-busy="true">
          <span className="h-9 w-9 animate-pulse rounded-full" style={{ background: SOFT_TINT }} />
          <span className="h-3 w-40 animate-pulse rounded-full" style={{ background: SOFT_TINT }} />
        </div>
      ) : (
        <>
          <div className="mt-2 flex items-center gap-3">
            <span
              aria-hidden
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-[14px] font-bold"
              style={kind === "google" ? { ...CONTROL, color: "#4285F4" } : { background: SOFT_TINT, color: NAVY }}
            >
              {kind === "google" ? "G" : <ShieldCheck size={16} />}
            </span>
            <div className="min-w-0">
              <p className="text-[14px] font-semibold" style={{ color: NAVY }}>
                {label}
              </p>
              {/* clamp-ok: guest's own email */}
              {email ? (
                <p className="truncate text-[12.5px]" style={{ color: MUTED }}>
                  {email}
                </p>
              ) : null}
            </div>
          </div>
          <p className="mt-3 text-[13px] font-medium" style={{ color: MUTED }} data-testid="password-managed">
            {kind === "code"
              ? t("account_modal.security_code_only")
              : t("account_modal.security_managed_by", { provider: kind === "google" ? "Google" : "Apple" })}
          </p>
        </>
      )}
    </Block>
  );
}

function OtherDevices() {
  const { t } = useTranslation();
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");

  async function run() {
    setState("sending");
    try {
      const res = await authClient.revokeOtherSessions();
      setState(res?.error ? "error" : "done");
    } catch {
      setState("error");
    }
  }

  return (
    <Block testId="security-devices">
      <Eyebrow>{t("account_modal.security_devices_title")}</Eyebrow>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <MonitorSmartphone size={18} color={MUTED} aria-hidden />
        <p
          role={state === "done" ? "status" : undefined}
          className="min-w-0 flex-1 text-[13px]"
          style={{ color: state === "done" ? GREEN : MUTED }}
        >
          {state === "done" ? t("account_modal.security_devices_done") : t("account_modal.security_devices_body")}
        </p>
        {state !== "done" ? (
          <button
            type="button"
            onClick={() => void run()}
            disabled={state === "sending"}
            className="mj-press inline-flex min-h-[40px] items-center gap-2 rounded-full px-3.5 text-[13px] font-semibold disabled:opacity-60"
            style={{ ...CONTROL, color: NAVY }}
          >
            {state === "sending" ? (
              <>
                <Spinner />
                {t("account.verify_sending")}
              </>
            ) : (
              t("account_modal.security_devices_button")
            )}
          </button>
        ) : null}
      </div>
      {state === "error" ? (
        <p role="alert" className="mj-fade-in mt-2 text-[12.5px]" style={{ color: RED }}>
          {t("account_modal.security_devices_error")}
        </p>
      ) : null}
    </Block>
  );
}

async function deleteMyAccount(): Promise<void> {
  unwrap(await api.me.account.delete());
}

/**
 * Delete account, with the app's confirmation (account.privacy.delete_confirm
 * copy, owner-approved): what goes, what stays, and a field that must read
 * DELETE before the red button wakes up. On success the guest is signed out
 * and `onDeleted` takes them home.
 */
export function DeleteAccountBlock({ onDeleted, bare = false }: { onDeleted: () => void; bare?: boolean }) {
  const { t } = useTranslation();
  const inputId = useId();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);
  const armed = typed.trim().toUpperCase() === DELETE_WORD;

  async function confirm() {
    if (!armed || sending) return;
    setSending(true);
    setError(false);
    try {
      await deleteMyAccount();
    } catch {
      setError(true);
      setSending(false);
      return;
    }
    // The account is gone; the session may already be too. Sign out anyway so
    // the cookie and the client's cache are cleared, and never let a failure
    // here keep the guest on a page for an account that no longer exists.
    try {
      await signOut();
    } catch {
      /* nothing left to sign out of */
    }
    onDeleted();
  }

  return (
    <Block testId="security-delete" bare={bare}>
      <Eyebrow>{t("account.delete_account")}</Eyebrow>
      {!open ? (
        // Always stacked: the explanation gets the full width and the button
        // sits under it. Side by side, a long translation squeezed the text
        // into a one-word column next to the button (French, 2026-10-12).
        // Guarded by account-security.test.tsx.
        <div className="mt-2 flex flex-col items-start gap-3" data-testid="security-delete-closed">
          <p className="text-[13px] font-medium leading-snug" style={{ color: MUTED }}>
            {t("account_modal.security_delete_body")}
          </p>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="mj-press inline-flex min-h-[40px] items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold"
            style={{ ...CONTROL, color: RED }}
          >
            <Trash2 size={14} aria-hidden /> {t("account.delete_account")}
          </button>
        </div>
      ) : (
        <div className="mj-fade-in mt-2" role="group" aria-labelledby={`${inputId}-title`}>
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full"
              style={{ background: RED_TINT, color: RED }}
            >
              <Trash2 size={18} />
            </span>
            <p id={`${inputId}-title`} className="font-heading text-[16px] font-bold" style={{ color: NAVY }}>
              {t("account_modal.delete_confirm.title")}
            </p>
          </div>
          <ul className="mt-2.5 flex flex-col gap-1.5">
            {(["bullet_bookings", "bullet_points", "bullet_permanent"] as const).map((k) => (
              <li key={k} className="flex items-start gap-2 text-[13px]" style={{ color: MUTED }}>
                <span aria-hidden className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: RED }} />
                {t(`account_modal.delete_confirm.${k}`)}
              </li>
            ))}
          </ul>
          <p
            className="mt-2.5 rounded-[12px] px-3 py-2 text-[12.5px] font-semibold"
            style={{ background: "rgba(229,178,101,0.18)", color: "#6b5326" }}
          >
            {t("account_modal.delete_confirm.reassurance")}
          </p>
          <label htmlFor={inputId} className="mt-3 block text-[12px] font-semibold" style={{ color: MUTED }}>
            {t("account_modal.delete_confirm.type_delete")}
          </label>
          <input
            id={inputId}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={DELETE_WORD}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            dir="ltr"
            className="mt-1 min-h-[44px] w-full rounded-[12px] px-3 text-[14px] font-semibold tracking-[0.12em] outline-none"
            style={{ ...CONTROL, color: NAVY }}
          />
          {error ? (
            <p role="alert" className="mj-fade-in mt-2 text-[12.5px]" style={{ color: RED }}>
              {t("account_modal.delete_error")}
            </p>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setTyped("");
                setError(false);
              }}
              className="mj-press min-h-[44px] flex-1 rounded-full px-4 text-[13.5px] font-semibold"
              style={{ ...CONTROL, color: NAVY }}
            >
              {t("account_modal.delete_confirm.keep")}
            </button>
            <button
              type="button"
              disabled={!armed || sending}
              onClick={() => void confirm()}
              className="mj-press inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full px-4 text-[13.5px] font-semibold text-white transition-opacity disabled:opacity-35"
              style={{ background: RED }}
            >
              {sending ? (
                <>
                  <Spinner />
                  {t("account_modal.deleting")}
                </>
              ) : (
                t("account_modal.delete_confirm.delete")
              )}
            </button>
          </div>
        </div>
      )}
    </Block>
  );
}

export function SecurityPanel({ email, onDeleted }: { email: string | null; onDeleted: () => void }) {
  return (
    <div className="flex flex-col gap-4">
      <HowYouSignIn email={email} />
      <OtherDevices />
      <DeleteAccountBlock onDeleted={onDeleted} />
    </div>
  );
}
